'use client';

import { useState } from 'react';
import Link from 'next/link';
import {
  Clock,
  Calendar,
  Ticket,
  MapPin,
  ArrowUpRight,
  Bookmark,
  Share2,
  Check,
  Pencil,
  BarChart3,
  ChevronRight,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { WorkshopLink, WorkshopPost } from '@/lib/workshops/types';
import PosterIdentity from './PosterIdentity';
import VideoUnit from './VideoUnit';
import MediaPlaceholder from './MediaPlaceholder';
import StatusChip, { ctr } from './StatusChip';

function OwnerMini({ value, label, accent }: { value: string; label: string; accent?: boolean }) {
  return (
    <div>
      <div className={cn('text-[17px] font-bold tabular-nums', accent ? 'text-accent' : 'text-text-primary')}>
        {value}
      </div>
      <span className="block font-mono uppercase text-[10px] font-semibold tracking-wider text-text-tertiary">
        {label}
      </span>
    </div>
  );
}

function Mono({ children }: { children: React.ReactNode }) {
  return (
    <span className="block font-mono uppercase text-[10px] font-semibold tracking-wider text-text-tertiary">
      {children}
    </span>
  );
}

function MetaItem({ icon: Icon, label }: { icon: typeof Clock; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[13px] font-medium text-text-secondary">
      <Icon className="w-[15px] h-[15px] text-text-tertiary" strokeWidth={1.75} />
      {label}
    </span>
  );
}

function PhotoStrip({ count }: { count: number }) {
  return (
    <div
      className="grid gap-2"
      style={{ gridTemplateColumns: count >= 3 ? '2fr 1fr' : '1fr', height: 320 }}
    >
      <div className="rounded-md overflow-hidden">
        <MediaPlaceholder label="Hero photo" icon="image" />
      </div>
      {count >= 3 && (
        <div className="grid gap-2" style={{ gridTemplateRows: '1fr 1fr' }}>
          <div className="rounded-md overflow-hidden">
            <MediaPlaceholder label="Photo" icon="image" />
          </div>
          <div className="rounded-md overflow-hidden relative">
            <MediaPlaceholder label="Photo" icon="image" />
            <div className="absolute inset-0 grid place-items-center text-white text-base font-bold" style={{ background: 'rgba(8,10,18,.5)' }}>
              +{count - 2}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function OutboundCTA({
  link,
  onClick,
}: {
  link: WorkshopLink;
  onClick: () => void;
}) {
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="inline-flex items-center justify-center gap-2 h-11 px-[18px] rounded-sm bg-accent text-text-on-accent text-[15px] font-semibold hover:bg-accent-hover whitespace-nowrap"
    >
      {link.label}
      <ArrowUpRight className="w-[17px] h-[17px]" strokeWidth={2} />
    </a>
  );
}

function SecondaryLink({
  link,
  onClick,
}: {
  link: WorkshopLink;
  onClick: () => void;
}) {
  return (
    <a
      href={link.url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={onClick}
      className="group/sl flex items-center gap-2.5 px-[11px] py-[9px] rounded-sm border border-border-subtle bg-surface-1 hover:border-border-strong"
    >
      <ArrowUpRight className="w-[15px] h-[15px] text-text-tertiary flex-shrink-0" strokeWidth={1.75} />
      <span className="flex-1 min-w-0 text-[13px] font-medium text-text-primary truncate group-hover/sl:text-accent">
        {link.label}
      </span>
      {link.host && <span className="font-mono text-[10.5px] text-text-tertiary">{link.host}</span>}
    </a>
  );
}

export default function DetailPane({
  post,
  onLinkClick,
  onPlay,
  onUnmute,
  showInlineCta = true,
  viewerId,
}: {
  post: WorkshopPost;
  onLinkClick: (link: WorkshopLink) => void;
  onPlay?: () => void;
  onUnmute?: () => void;
  showInlineCta?: boolean;
  // When the viewer owns this post, show the manage bar instead of Save/Share.
  viewerId?: string | null;
}) {
  const [copied, setCopied] = useState(false);
  const isOwner = !!viewerId && post.instructorId === viewerId;
  const primary = post.links.find((l) => l.isPrimary) || post.links[0] || null;
  const secondary = post.links.filter((l) => l.id !== primary?.id);
  const paragraphs = (post.description || '')
    .split(/\n\s*\n/)
    .map((p) => p.trim())
    .filter(Boolean);
  const leaveHost = primary?.host || (() => {
    try {
      return primary ? new URL(primary.url).hostname.replace(/^www\./, '') : '';
    } catch {
      return '';
    }
  })();

  const share = async () => {
    try {
      const url = `${window.location.origin}/workshops?post=${post.id}`;
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked — no-op */
    }
  };

  return (
    <div className="px-5 sm:px-[30px] pt-6 pb-[30px] max-w-[760px]">
      {post.promoted && (
        <div className="mb-2.5">
          <span className="inline-flex items-center gap-1.5 text-text-tertiary font-mono uppercase text-[10px] font-semibold tracking-wider">
            <span className="w-1 h-1 rounded-full bg-text-tertiary" aria-hidden />
            {post.sponsorWord || 'Promoted'}
          </span>
        </div>
      )}

      <h2 className="m-0 text-[26px] font-bold leading-[1.2] tracking-[-0.02em] text-text-primary max-w-[680px]">
        {post.title}
      </h2>

      <div className="flex flex-wrap items-center gap-2.5 mt-4">
        <PosterIdentity poster={post.poster} size="lg" showTagline />
        <span className="flex-1" />
        {isOwner ? (
          <>
            {/* Owner manage bar. Board posts are live, so the chip reads Live. */}
            <StatusChip status="live" />
            <Link
              href={`/workshops/${post.id}/edit`}
              className="inline-flex items-center gap-1.5 h-[34px] px-[13px] rounded-sm bg-surface-2 border border-border-default text-text-secondary text-[13px] font-medium hover:bg-surface-3"
            >
              <Pencil className="w-[15px] h-[15px]" strokeWidth={1.75} /> Edit post
            </Link>
            <Link
              href="/workshops/dashboard"
              className="inline-flex items-center gap-1.5 h-[34px] px-3.5 rounded-sm bg-accent text-text-on-accent text-[13px] font-semibold hover:bg-accent-hover"
            >
              <BarChart3 className="w-[15px] h-[15px]" strokeWidth={1.75} /> View analytics
            </Link>
          </>
        ) : (
          <>
            {/* Save is a Phase-2 stub (no per-user saves table yet). */}
            <button
              type="button"
              disabled
              title="Saving is coming soon"
              className="inline-flex items-center gap-1.5 h-[34px] px-[13px] rounded-sm bg-surface-2 border border-border-default text-text-secondary text-[13px] font-medium opacity-50 cursor-not-allowed"
            >
              <Bookmark className="w-[15px] h-[15px]" strokeWidth={1.75} /> Save
            </button>
            <button
              type="button"
              onClick={share}
              aria-label="Copy link to this workshop"
              className="w-[34px] h-[34px] rounded-sm bg-surface-2 border border-border-default text-text-secondary grid place-items-center hover:bg-surface-3"
            >
              {copied ? <Check className="w-[15px] h-[15px] text-status-online" strokeWidth={2} /> : <Share2 className="w-[15px] h-[15px]" strokeWidth={1.75} />}
            </button>
          </>
        )}
      </div>

      {isOwner && (
        <div
          className="flex flex-wrap items-center gap-x-7 gap-y-2 mt-4 px-[18px] py-3 rounded-md bg-surface-2 border border-border-default"
          style={{ boxShadow: 'var(--elev-card)' }}
        >
          <OwnerMini value={post.stats.views.toLocaleString()} label="views" />
          <OwnerMini value={post.stats.clicks.toLocaleString()} label="outbound clicks" accent />
          <OwnerMini value={ctr(post.stats.clicks, post.stats.views)} label="CTR" />
          <OwnerMini value={post.stats.saves.toLocaleString()} label="saves" />
          <span className="flex-1" />
          <Link
            href="/workshops/dashboard"
            className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-accent hover:text-accent-hover"
          >
            Full analytics <ChevronRight className="w-3.5 h-3.5" strokeWidth={2} />
          </Link>
        </div>
      )}

      {/* meta strip — every item optional */}
      {(post.formatText || post.whenText || post.seatsText || post.host || post.priceText) && (
        <div className="flex flex-wrap items-center gap-4 mt-[18px] pb-[18px] border-b border-border-subtle">
          {post.formatText && <MetaItem icon={Clock} label={post.formatText} />}
          {post.whenText && <MetaItem icon={Calendar} label={post.whenText} />}
          {post.seatsText && <MetaItem icon={Ticket} label={post.seatsText} />}
          {post.host && <MetaItem icon={MapPin} label={`via ${post.host}`} />}
          {post.priceText && (
            <span
              className={cn(
                'inline-flex items-center text-[14px] font-bold',
                post.priceText === 'Free' ? 'text-status-online' : 'text-text-primary',
              )}
            >
              {post.priceText}
            </span>
          )}
        </div>
      )}

      {/* media — only when video or photos exist (never an empty frame) */}
      {(post.video || post.photoCount > 0) && (
        <div className="mt-5">
          {post.video ? (
            <VideoUnit post={{ id: post.id, video: post.video }} size="large" onPlay={onPlay} onUnmute={onUnmute} />
          ) : (
            <PhotoStrip count={post.photoCount} />
          )}
        </div>
      )}

      {/* primary outbound CTA + leave notice */}
      {showInlineCta && primary && (
        <div className="mt-5 flex flex-wrap items-center gap-3.5">
          <OutboundCTA link={primary} onClick={() => onLinkClick(primary)} />
          <span className="inline-flex items-center gap-1.5 text-[12.5px] text-text-tertiary">
            <ArrowUpRight className="w-[13px] h-[13px]" strokeWidth={1.75} />
            Opens {leaveHost || 'an external site'} — you&apos;ll leave Indiestry
          </span>
        </div>
      )}

      {/* about */}
      {paragraphs.length > 0 && (
        <div className="mt-6 max-w-[660px]">
          <Mono>About this workshop</Mono>
          <div className="mt-2.5">
            {paragraphs.map((p, i) => (
              <p key={i} className="m-0 mb-3 text-[14.5px] leading-[1.6] text-text-secondary">
                {p}
              </p>
            ))}
          </div>
        </div>
      )}

      {/* agenda */}
      {post.agenda.length > 0 && (
        <div className="mt-[18px] max-w-[660px]">
          <Mono>What we cover</Mono>
          <div className="mt-2.5 grid grid-cols-1 sm:grid-cols-2 gap-[9px]">
            {post.agenda.map((a, i) => (
              <div
                key={i}
                className="flex items-center gap-2.5 px-3 py-2.5 rounded-sm bg-surface-2 border border-border-subtle"
              >
                <span className="w-5 h-5 rounded-full flex-shrink-0 bg-accent-soft text-accent grid place-items-center font-mono text-[10px] font-bold">
                  {i + 1}
                </span>
                <span className="text-[13px] text-text-primary">{a}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* secondary outbound links */}
      {secondary.length > 0 && (
        <div className="mt-[18px] max-w-[660px]">
          <Mono>More from this poster</Mono>
          <div className="mt-2.5 flex flex-col gap-2">
            {secondary.map((l) => (
              <SecondaryLink key={l.id} link={l} onClick={() => onLinkClick(l)} />
            ))}
          </div>
        </div>
      )}

      {/* tags */}
      {post.tags.length > 0 && (
        <div className="mt-[18px] flex flex-wrap gap-1.5">
          {post.tags.slice(0, 6).map((t) => (
            <span
              key={t}
              className="inline-flex items-center h-[22px] px-2 rounded-sm bg-surface-3 text-text-secondary border border-border-subtle text-[11px] font-medium"
            >
              #{t}
            </span>
          ))}
        </div>
      )}

      {/* listing disclaimer */}
      <div className="mt-5 flex gap-2.5 px-3.5 py-3 max-w-[660px] rounded-sm bg-surface-2 border border-border-subtle">
        <ArrowUpRight className="w-[15px] h-[15px] text-text-tertiary flex-shrink-0 mt-px" strokeWidth={1.75} />
        <span className="text-[12px] leading-[1.5] text-text-tertiary">
          Indiestry hosts this <b className="text-text-secondary">listing</b>, not the class. There&apos;s no
          on-platform enrollment — registration, payment, and attendance happen on the poster&apos;s own site.
        </span>
      </div>
    </div>
  );
}
